import { makeStyles } from '@material-ui/styles'
import { useEffect } from 'react'
import CopyToClipboard from 'core/components/CopyToClipboard'
import Button from 'core/elements/button/Button'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import {
  areEtcdBackupSchedulesDeployed,
  describeEtcdBackupSchedule,
  describeTimeZone,
  ETCD_BACKUP_DEFAULT_MAX_COUNT,
  ETCD_BACKUP_DEFAULT_STORAGE_PATH,
  ETCD_BACKUP_SCHEDULE_LABELS,
  type EtcdBackupScheduleName,
  formatBackupRun,
  getEtcdBackupTimeZone,
  getNextEtcdBackup,
} from 'k8s/components/kaapi/cluster-addons/etcd-backup'
import { AddonStatusBadge } from './AddonStatusBadge'
import type { EtcdBackupAddon } from './ConfigureEtcdBackupModal'

interface BackupDetailsModalProps {
  addon: EtcdBackupAddon
  onClose: () => void
}

// BDM-FR-001: the Backup add-on's status, last backup and settings, opened from the Details
// link on the Add-ons tab. Errors stay behind the status badge's error modal.
export default function BackupDetailsModal({ addon, onClose }: BackupDetailsModalProps) {
  const classes = useStyles()
  const config = addon.spec?.clusterSpec?.etcdBackup
  const status = addon.status
  // BDM-FR-005: times are in the add-on's time zone, the one its schedules run in
  const timeZone = getEtcdBackupTimeZone(config?.timeZone)
  const last = status?.lastBackup
  const lastBackup = last && formatBackupRun(last, timeZone)
  const nextRun =
    areEtcdBackupSchedulesDeployed(status) && getNextEtcdBackup(config?.schedules, config?.timeZone)
  const nextBackup = nextRun && formatBackupRun(nextRun, timeZone)

  // BDM-FR-004: Esc closes the modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <Modal
      title="Backup Details"
      onClose={onClose}
      maxWidth={800}
      panel="dialog"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
      open
    >
      <div className={classes.content} data-testid="backup-details">
        <div className={classes.row}>
          <Text variant="caption1">Status</Text>
          <AddonStatusBadge addonName="Backup" status={status} />
        </div>

        {/* BDM-FR-002: when, by which schedule and where the last backup was saved */}
        <div className={classes.section}>
          <Text variant="caption1">Last Backup</Text>
          {last && lastBackup ? (
            <>
              <Text variant="body2" data-testid="backup-details-last">
                {`${lastBackup.relative}, at ${lastBackup.exact}, by the ${lastBackup.schedule.toLowerCase()} schedule${last.node ? ` on ${last.node}` : ''}`}
              </Text>
              {last.path && (
                <CopyToClipboard copyText={last.path} inline codeBlock={false} triggerWithChild>
                  <Text variant="body2" className={classes.path} data-testid="backup-details-path">
                    {last.path}
                  </Text>
                </CopyToClipboard>
              )}
            </>
          ) : (
            <Text variant="body2" className={classes.passive} data-testid="backup-details-last">
              No backup has finished yet
            </Text>
          )}
        </div>

        {/* BDM-FR-005: the next run, across the schedules */}
        {nextBackup && (
          <div className={classes.section}>
            <Text variant="caption1">Next Backup</Text>
            <Text variant="body2" data-testid="backup-details-next">
              {`${nextBackup.exact}, by the ${nextBackup.schedule.toLowerCase()} schedule (${nextBackup.relative})`}
            </Text>
          </div>
        )}

        {/* BDM-FR-003: the schedules, each with how many backups a node keeps */}
        <div className={classes.section}>
          <Text variant="caption1">Schedules</Text>
          {(config?.schedules ?? []).map((s) => (
            <div key={s.name} className={classes.schedule} data-testid="backup-details-schedule">
              <Text variant="body2" className={classes.scheduleName}>
                {ETCD_BACKUP_SCHEDULE_LABELS[s.name as EtcdBackupScheduleName] ?? s.name}
              </Text>
              <Text variant="body2">
                {describeEtcdBackupSchedule(s.name ?? '', s.schedule, timeZone)}
              </Text>
              <Text variant="body2" className={classes.passive}>
                {`Keeps ${s.maxCount ?? ETCD_BACKUP_DEFAULT_MAX_COUNT} per node`}
              </Text>
            </div>
          ))}
        </div>

        <div className={classes.section}>
          <Text variant="body2" data-testid="backup-details-time-zone">
            {`Time zone: ${describeTimeZone(timeZone)}`}
          </Text>
          <Text variant="body2" data-testid="backup-details-storage">
            {`Storage path: ${config?.storagePath || ETCD_BACKUP_DEFAULT_STORAGE_PATH}, a directory per schedule on each control plane node`}
          </Text>
          {/* BDM-FR-003: backups are on the nodes' disks */}
          <Text variant="body2" className={classes.passive} data-testid="backup-details-node-local">
            Backups are lost when their node is replaced, as in a Kubernetes upgrade or node repair.
            Copy the ones you need elsewhere.
          </Text>
        </div>
      </div>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  content: {
    display: 'grid',
    gridGap: theme.spacing(3),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  section: {
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  schedule: {
    display: 'grid',
    gridTemplateColumns: '96px minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'baseline',
  },
  scheduleName: {
    fontWeight: 600,
  },
  path: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  passive: {
    color: theme.components.typography.passive,
  },
}))
